import { t } from "./i18n.js";

export function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

export function formatAction(action: string): string {
  const keyMap: Record<string, string> = {
    login: "action_authentication",
    payment: "action_payment",
    signing: "action_signing",
    card_payment: "action_card_payment",
    key_registration: "action_key_registration",
    text_approval: "action_text_approval",
    confirmation: "action_confirmation",
    legacy_payment: "action_legacy_payment",
    approve_card: "action_approve_card",
    approve_account: "action_approve_account",
    customer_service_call: "action_customer_service_call",
    customer_service_chat: "action_customer_service_chat",
  };
  const key = keyMap[action];
  return key ? t(key) : action;
}

export function formatFlow(flowType: string): string {
  const keyMap: Record<string, string> = {
    authorization: "flow_authorization",
    app2app: "flow_app2app",
    iframe: "flow_iframe",
    ciba: "flow_ciba",
    signup: "flow_signup",
  };
  const key = keyMap[flowType];
  return key ? t(key) : flowType;
}

export function formatMethod(amrs: string[]): string | null {
  if (amrs.includes("bid-otp")) {
    return t("method_code_device");
  }
  if (amrs.includes("bid-app") || amrs.includes("bis-hwk")) {
    return t("method_app");
  }
  return null;
}

export function isCancelled(status: string): boolean {
  return status === "failed" || status === "abandoned";
}

/**
 * Action label for list rows and the detail pill. Successful activities show the
 * bare action ("Login"); cancelled ones (failed/abandoned) are rendered as a
 * single localised string ("Cancelled login") whose word order the translation
 * controls via the {action} placeholder.
 */
export function formatActionWithStatus(action: string, status: string): string {
  const label = formatAction(action);
  if (!isCancelled(status)) {
    return label;
  }
  const lowercasedFirst = label.charAt(0).toLowerCase() + label.slice(1);
  return t("status_cancelled_action", { action: lowercasedFirst });
}

/**
 * Full status label for the detail view's Status row, where every activity
 * shows a status. Cancelled activities (failed/abandoned) collapse to a single
 * "Cancelled" label.
 */
export function formatStatusFull(status: string): string {
  if (status === "success") {
    return t("status_success");
  }
  if (isCancelled(status)) {
    return t("status_cancelled");
  }
  return status;
}

export function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return "\u2014";
  }
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return t("time_now");
  if (diffMins < 60) return t("time_min_ago", { n: diffMins });
  if (diffHours < 24) {
    return diffHours === 1
      ? t("time_hour_ago", { n: diffHours })
      : t("time_hours_ago", { n: diffHours });
  }
  if (diffDays === 1) return t("time_yesterday");
  if (diffDays < 7) return t("time_days_ago", { n: diffDays });

  const day = date.getDate();
  const month = date.toLocaleDateString(document.documentElement.lang, {
    month: "short",
  });
  const year = date.getFullYear();
  const currentYear = now.getFullYear();

  return year === currentYear ? `${day}. ${month}` : `${day}. ${month} ${year}`;
}

export function formatAbsoluteDate(isoString: string): string {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "\u2014";
  const lang = document.documentElement.lang || "en";
  const datePart = date.toLocaleDateString(lang, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString(lang, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return t("datetime_format", { date: datePart, time: timePart });
}

export function formatShortDate(isoString: string): string {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "\u2014";
  return date.toLocaleDateString(document.documentElement.lang, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Extract values for a specific tag prefix from the tags array */
export function getTagValues(tags: string[], prefix: string): string[] {
  const p = `${prefix}:`;
  return tags.filter((t) => t.startsWith(p)).map((t) => t.slice(p.length));
}
